import { AxiosError } from 'axios';
import { useMutation, useQueryClient } from 'react-query';
import { Axios } from '@utils/api/axios-instance';
import { BaseError } from '@utils/types/shared/base';
import { DeleteAdminOrganizationUserResponse } from '@utils/types/admin';
import { ADMIN_ORGANIZATION_USERS_QUERY_KEY } from './use-create-admin-organization-user';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

type DeleteUserParams = {
  organizationId: number | string;
  userId: number | string;
};

/**
 * Hook to delete a user from an on-prem organization
 *
 * DELETE /api/admin/on-prem/organizations/{organization_id}/users/{user_id}
 */
export const useDeleteAdminOrganizationUser = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const deleteUser = async ({ organizationId, userId }: DeleteUserParams) => {
    const url = `api/admin/on-prem/organizations/${organizationId}/users/${userId}`;
    const response =
      await Axios.delete<DeleteAdminOrganizationUserResponse>(url);
    return response.data;
  };

  return useMutation<
    DeleteAdminOrganizationUserResponse,
    AxiosError<BaseError>,
    DeleteUserParams
  >(deleteUser, {
    onSuccess: (_, variables) => {
      toast.success(t('user-deleted-successfully'));
      queryClient.invalidateQueries([
        ADMIN_ORGANIZATION_USERS_QUERY_KEY,
        variables.organizationId
      ]);
      queryClient.invalidateQueries([
        ADMIN_ORGANIZATION_USERS_QUERY_KEY,
        String(variables.organizationId)
      ]);
    },
    onError: (error: AxiosError<BaseError>) => {
      const errorMessage =
        error.response?.data?.error_description ||
        error.response?.data?.detail ||
        error.response?.data?.error ||
        error.response?.data?.message ||
        t('failed-to-delete-user');
      toast.error(String(errorMessage));
    }
  });
};
